import { Check } from "lucide-react";
import { AppStoreBadge } from "@/components/Brand";

const tiers = [
  {
    name: "Free",
    price: "$0",
    period: "",
    tag: "Get Started",
    variant: "free",
    features: [
      "Sample beginner program",
      "Limited exercise previews",
      "Sign in with Apple",
    ],
  },
  {
    name: "Premium",
    price: "$14.99",
    period: "/ month",
    tag: "Most Popular",
    variant: "premium",
    features: [
      "Full 250+ program library",
      "Sport-specific training paths",
      "Full 200+ exercise library",
      "Progress dashboard & charts",
      "Readiness-adjusted workouts",
      "Monthly testing prompts",
      "AI Coach (Claude / GPT)",
    ],
  },
  {
    name: "Elite",
    price: "$39.99",
    period: "/ month",
    tag: "Most Complete",
    variant: "elite",
    features: [
      "Everything in Premium",
      "Advanced + elite/pro programs",
      "Sport KPI analytics",
      "Coach review request",
      "Custom program request",
    ],
  },
];

export default function SubscriptionSection() {
  return (
    <section
      id="pricing"
      className="relative nfp-section bg-[#070707]"
      data-testid="section-pricing"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#1FA2FF] mb-4">
            Subscriptions
          </p>
          <h2 className="nfp-heading text-white text-4xl md:text-6xl leading-[1] tracking-tight">
            Three tiers. <br />
            One <span className="text-[#00E5FF]">performance</span> standard.
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed">
            Subscriptions are managed through Apple In-App Purchase inside the
            NFP iOS app — no external checkout, no website billing.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((t) => {
            const isElite = t.variant === "elite";
            const isFree = t.variant === "free";
            return (
              <div
                key={t.name}
                data-testid={`pricing-${t.name.toLowerCase()}`}
                className={`relative rounded-3xl p-8 md:p-10 ${
                  isElite
                    ? "nfp-elite-border"
                    : "border border-white/10"
                }`}
                style={{
                  background: isElite
                    ? "linear-gradient(180deg,#0A1A33 0%,#0a0a0a 100%)"
                    : "#0c0c0c",
                }}
              >
                <div className="flex items-center justify-between mb-6">
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border ${
                      isElite
                        ? "border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/5"
                        : isFree
                        ? "border-white/15 text-white/60"
                        : "border-[#1FA2FF]/40 text-[#1FA2FF] bg-[#1FA2FF]/5"
                    }`}
                  >
                    {t.tag}
                  </span>
                </div>

                <h3 className="nfp-heading text-white text-3xl md:text-4xl tracking-tight">
                  {t.name}
                </h3>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="nfp-heading text-5xl md:text-6xl text-white">
                    {t.price}
                  </span>
                  {t.period && (
                    <span className="text-white/50 text-sm">{t.period}</span>
                  )}
                </div>
                {!isFree && (
                  <p className="mt-1 text-xs text-white/40 tracking-wide">
                    Billed monthly via Apple In-App Purchase
                  </p>
                )}
                {isFree && (
                  <p className="mt-1 text-xs text-white/40 tracking-wide">
                    No credit card. Get a taste of NFP.
                  </p>
                )}

                <ul className="mt-7 space-y-3">
                  {t.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-sm text-white/80"
                    >
                      <span
                        className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                          isElite
                            ? "bg-[#D4AF37]/15 text-[#D4AF37]"
                            : "bg-[#1FA2FF]/15 text-[#1FA2FF]"
                        }`}
                      >
                        <Check className="w-3 h-3" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <AppStoreBadge
                    testId={`pricing-${t.name.toLowerCase()}-cta`}
                    variant={isElite ? "primary" : "secondary"}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-white/45 max-w-3xl mx-auto leading-relaxed">
          Subscriptions auto-renew monthly unless canceled at least 24 hours
          before the end of the current billing period. Payment is charged to
          your Apple ID at confirmation of purchase. You can manage or cancel
          anytime in your Apple ID subscription settings.
        </p>
      </div>
    </section>
  );
}
